import { useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

const SORT_PATTERN = /^(.+):(asc|desc)$/;

/**
 * A table's search, filter and sort kept in the page's query string under
 * `prefix`, so opening a row and coming back (or reloading) shows the same
 * rows. Pass the result to `useTableControls` as `persist`. Values are read
 * once, when the table mounts; `save` rewrites only this table's keys and
 * replaces the history entry so typing does not fill the back stack.
 */
export function useTableParams(prefix) {
    const [params, setParams] = useSearchParams();
    const initial = useRef(null);
    if (initial.current === null) {
        const sort = SORT_PATTERN.exec(params.get(`${prefix}-sort`) || '');
        initial.current = {
            search: params.get(`${prefix}-q`) || '',
            status: params.get(`${prefix}-filter`) || 'all',
            department: params.get(`${prefix}-dept`) || 'all',
            sort: sort
                ? { key: sort[1], direction: sort[2] }
                : { key: null, direction: 'asc' },
        };
    }

    const save = useCallback(
        ({ search, status, department = 'all', sort }) => {
            setParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    const write = (name, value) =>
                        value ? next.set(name, value) : next.delete(name);
                    write(`${prefix}-q`, search);
                    write(`${prefix}-filter`, status === 'all' ? '' : status);
                    write(
                        `${prefix}-dept`,
                        department === 'all' ? '' : department
                    );
                    write(
                        `${prefix}-sort`,
                        sort.key ? `${sort.key}:${sort.direction}` : ''
                    );
                    return next;
                },
                { replace: true }
            );
        },
        [prefix, setParams]
    );

    return { initial: initial.current, save };
}

/**
 * The selected tab of a dashboard, kept in the `name` query parameter so a
 * detail page's Back returns to the same tab. Returns [tab, selectTab].
 */
export function useTabParam(defaultTab, name = 'tab') {
    const [params, setParams] = useSearchParams();
    const selectTab = useCallback(
        (tab) =>
            setParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set(name, tab);
                    return next;
                },
                { replace: true }
            ),
        [name, setParams]
    );
    return [params.get(name) || defaultTab, selectTab];
}

export default useTableParams;
